// src/utils/markupApi.js — DropPin plan-markup SERVER persistence (best-effort).
// ---------------------------------------------------------------------------
// A thin client for /api/markup, keyed per DRAWING (the tenant is resolved
// server-side from the session cookie — never trusted from the client). Used by
// useMarkup as an OPTIONAL layer on top of its always-on localStorage cache:
//   • load(drawingId)  -> the saved markup doc, or null (no server copy yet /
//                         endpoint absent) — null makes useMarkup keep its
//                         localStorage copy, so a 404 never wipes local edits.
//   • save(drawingId, doc) -> best-effort PUT; throws on a non-2xx so useMarkup
//                         can swallow it and fall back to localStorage-only.
// Plain fetch (same-origin cookie) — deliberately NOT the shared apiSend, so
// markup saves stay self-contained and never entangle the global offline outbox.
// ---------------------------------------------------------------------------

const BASE = '/api/markup';

export async function loadMarkup(drawingId) {
  if (drawingId == null) return null;
  let r;
  try {
    r = await fetch(`${BASE}?drawingId=${encodeURIComponent(drawingId)}`, { credentials: 'same-origin' });
  } catch (_e) {
    throw new Error('markup load: network'); // offline -> useMarkup keeps localStorage
  }
  if (r.status === 404) return null;            // no server copy yet / endpoint absent
  if (!r.ok) throw new Error(`markup load: ${r.status}`);
  let j = null;
  try { j = await r.json(); } catch (_e) { return null; }
  return (j && j.doc) || null;
}

export async function saveMarkup(drawingId, doc) {
  if (drawingId == null) return false;
  const r = await fetch(`${BASE}?drawingId=${encodeURIComponent(drawingId)}`, {
    method: 'PUT',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ doc }),
  });
  if (!r.ok) throw new Error(`markup save: ${r.status}`);
  return true;
}

// Build the { load, save } adapter useMarkup expects, or null when there is no
// drawing to key on (falls back to localStorage-only).
export function markupRemoteFor(drawingId) {
  if (drawingId == null) return null;
  return {
    load: () => loadMarkup(drawingId),
    save: (doc) => saveMarkup(drawingId, doc),
  };
}
